import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('token'));

  // Set up axios interceptor for token and deactivation detection
  useEffect(() => {
    if (token) {
      authAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete authAPI.defaults.headers.common['Authorization'];
      delete api.defaults.headers.common['Authorization'];
    }

    // Add response interceptor to detect account deactivation and session termination
    const interceptor = authAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if account was deactivated
        if (error.response?.status === 401 && error.response?.data?.deactivated) {
          // Immediately log out and show message
          sessionStorage.removeItem('token');
          setToken(null);
          setUser(null);
          delete authAPI.defaults.headers.common['Authorization'];
          toast.error('Your account has been deactivated by an administrator');
          // Redirect to login
          window.location.href = '/login';
        } else if (error.response?.status === 401 && error.response?.data?.sessionTerminated) {
          // Session terminated by login block
          sessionStorage.removeItem('token');
          setToken(null);
          setUser(null);
          delete authAPI.defaults.headers.common['Authorization'];
          toast.error('Your session has been terminated. Please login again.');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      authAPI.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Verify token on app load
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.get('/reseller-auth/verify');
        if (response.data.success) {
          setUser(response.data.data.user);
        } else {
          // Token is invalid
          logout();
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        if (error.response?.status === 401) {
          logout(true, 'timeout');
        } else {
          logout(false);
        }
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  // Check for session status every 2 seconds for instant deactivation detection
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(async () => {
      try {
        const response = await authAPI.get('/reseller-auth/verify');
        if (!response.data.success) {
          logout(true, 'timeout');
        }
      } catch (error) {
        if (error.response?.status === 401) {
          // Check if account was deactivated
          if (error.response?.data?.deactivated) {
            sessionStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete authAPI.defaults.headers.common['Authorization'];
            toast.error('Your account has been deactivated by an administrator');
            window.location.href = '/login';
          } else if (error.response?.data?.sessionTerminated) {
            // Session terminated by login block
            sessionStorage.removeItem('token');
            setToken(null);
            setUser(null);
            delete authAPI.defaults.headers.common['Authorization'];
            toast.error('Your session has been terminated. Please login again.');
            window.location.href = '/login';
          } else {
            logout(true, 'timeout');
          }
        }
      }
    }, 2000); // Check every 2 seconds for instant deactivation

    return () => clearInterval(interval);
  }, [token]);

  const login = async (credentials) => {
    try {
      const response = await authAPI.post('/reseller-auth/login', credentials);
      
      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requiresTwoFactor) {
          return {
            success: true,
            requiresTwoFactor: true,
            message: response.data.message,
            discord_id: response.data.data.discord_id,
            discord_username: response.data.data.discord_username
          };
        }
        
        // Direct login (trusted IP - 2FA skipped)
        const { token: newToken, user: userData } = response.data.data;
        
        // Store token in sessionStorage (clears on browser close)
        sessionStorage.setItem('token', newToken);
        
        // IMMEDIATELY set the token in axios headers to prevent race condition
        authAPI.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        setToken(newToken);
        setUser(userData);
        
        toast.success('Login successful!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Login failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const check2FAStatus = async (discord_id, showToast = false) => {
    try {
      const response = await authAPI.post('/reseller-auth/check-2fa', { discord_id });
      
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Store token in sessionStorage
        sessionStorage.setItem('token', newToken);
        
        // IMMEDIATELY set the token in axios headers to prevent race condition
        authAPI.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        setToken(newToken);
        setUser(userData);
        
        toast.success('Login successful!');
        return { success: true, verified: true };
      } else if (response.data.requiresVerification) {
        return { success: false, requiresVerification: true, message: response.data.message };
      } else {
        // Only show toast if explicitly requested (not during polling)
        if (showToast) {
          toast.error(response.data.message || 'Verification failed');
        }
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || '2FA verification failed';
      // Only show toast if explicitly requested (not during polling)
      if (showToast) {
        toast.error(message);
      }
      return { success: false, message };
    }
  };

  const get2FACode = async (discord_id) => {
    try {
      const response = await authAPI.post('/reseller-auth/get-2fa-code', { discord_id });
      
      if (response.data.success) {
        return { 
          success: true, 
          code: response.data.data.code, 
          nextRegeneration: response.data.data.nextRegeneration,
          message: response.data.data.message 
        };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to get 2FA code';
      return { success: false, message };
    }
  };

  const logout = (showMessage = true, reason = null) => {
    // Clear local state
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete authAPI.defaults.headers.common['Authorization'];
    
    if (showMessage) {
      if (reason === 'timeout') {
        toast.error('Session timed out. Please login again.');
      } else {
        toast.success('Logged out successfully');
      }
    }
  };

  const changePassword = async (passwords) => {
    try {
      const response = await authAPI.post('/auth/change-password', passwords);
      
      if (response.data.success) {
        toast.success('Password changed successfully');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Password change failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const getProfile = async () => {
    try {
      const response = await authAPI.get('/auth/profile');
      if (response.data.success) {
        return response.data.data.user;
      }
    } catch (error) {
      console.error('Failed to get profile:', error);
    }
    return null;
  };

  const value = {
    user,
    loading,
    token,
    login,
    logout,
    check2FAStatus,
    get2FACode,
    changePassword,
    getProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
